import { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../services/api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

// Persistent cache - no expiry, shared across all components
let cachedRelationshipRules = null;
let cachedRelationshipTypes = null;
let fetchPromise = null;

/**
 * Custom hook for managing relationship rules
 * Fetches relationship rules from the backend and provides utilities for frontend components
 */
export const useRelationshipRules = () => {
  const [relationshipRules, setRelationshipRules] = useState(
    cachedRelationshipRules || []
  );
  const [relationshipTypes, setRelationshipTypes] = useState(
    cachedRelationshipTypes || []
  );
  const [loading, setLoading] = useState(!cachedRelationshipRules);
  const [error, setError] = useState(null);

  // Fetch relationship rules and types (disabled: resolve via local SectionConfig/resolver)
  useEffect(() => {
    // If already cached, use it immediately
    if (cachedRelationshipRules && cachedRelationshipTypes) {
      console.log("✅ useRelationshipRules: Using cached data");
      setRelationshipRules(cachedRelationshipRules);
      setRelationshipTypes(cachedRelationshipTypes);
      setLoading(false);
      return;
    }

    // If already fetching, wait for the existing promise
    if (fetchPromise) {
      console.log("⏳ useRelationshipRules: Waiting for existing fetch");
      fetchPromise
        .then(() => {
          setRelationshipRules(cachedRelationshipRules);
          setRelationshipTypes(cachedRelationshipTypes);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
      return;
    }

    // Start new fetch
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("📋 useRelationshipRules: Fetching from API");

        // Fetch both relationship rules and types in parallel
        const [rulesResponse, typesResponse] = await Promise.all([
          apiFetch("/api/relationship-rules"),
          apiFetch("/api/relationship-types"),
        ]);

        if (rulesResponse.ok) {
          const rulesData = await rulesResponse.json();
          console.log("📋 Fetched relationship rules:", rulesData);
          const rules = rulesData.relationship_rules || [];
          setRelationshipRules(rules);
          // Cache it for future use
          cachedRelationshipRules = rules;
        } else {
          throw new Error("Failed to fetch relationship rules");
        }

        if (typesResponse.ok) {
          const typesData = await typesResponse.json();
          const types = typesData.relationship_types || [];
          setRelationshipTypes(types);
          // Cache it for future use
          cachedRelationshipTypes = types;
        } else {
          throw new Error("Failed to fetch relationship types");
        }
      } catch (err) {
        console.error("Error fetching relationship data:", err);
        setError(err.message);
        // Set fallback empty data
        setRelationshipRules([]);
        setRelationshipTypes([]);
      } finally {
        setLoading(false);
        fetchPromise = null; // Clear the promise so new instances can fetch if needed
      }
    };

    fetchPromise = fetchData();
  }, []);

  // Transform relationship rules into the format expected by frontend components
  const transformedRules = useMemo(() => {
    if (
      !relationshipRules ||
      !Array.isArray(relationshipRules) ||
      !relationshipRules.length
    )
      return {};

    const rulesByEntityType = {};

    relationshipRules.forEach((rule) => {
      if (RETIRED_INTERFACE_ENDPOINT_RULE_KEYS.has(rule.rule_key)) {
        return;
      }
      const sourceType = rule.source_entity_type?.name;
      const targetType = rule.target_entity_type?.name;
      const relationshipType = rule.relationship_type?.name;

      if (!sourceType || !targetType || !relationshipType) return;

      // Convert database entity type names to frontend format
      const frontendSourceType = convertEntityTypeName(sourceType);
      const frontendTargetType = convertEntityTypeName(targetType);

      // Store rules under source entity type (outgoing relationships)
      const sourceKeysToStore = [frontendSourceType, sourceType];
      sourceKeysToStore.forEach((key) => {
        if (!rulesByEntityType[key]) {
          rulesByEntityType[key] = [];
        }

        rulesByEntityType[key].push({
          key: rule.rule_key,
          visibleLabel: rule.visible_label,
          relatedEntityType: frontendTargetType,
          direction: "outgoing",
          relationshipType: relationshipType,
          description: rule.description,
          isActive: rule.is_active,
          isIncoming: false,
        });
      });

      // ALSO store rules under target entity type (incoming relationships)
      // This allows editing an entity to see both what it relates to AND what relates to it
      const targetKeysToStore = [frontendTargetType, targetType];
      targetKeysToStore.forEach((key) => {
        if (!rulesByEntityType[key]) {
          rulesByEntityType[key] = [];
        }

        // Create appropriate labels for incoming relationships based on the relationship type
        // Use display names for better UX (e.g., "Application" instead of "ApplicationComponent")
        const sourceDisplayName =
          frontendSourceType === "ApplicationComponent"
            ? "Application"
            : frontendSourceType;

        const relTypeLower = String(relationshipType || "").toLowerCase();
        let incomingLabel;
        if (relTypeLower === "provides" || relTypeLower === "is provided by") {
          incomingLabel = `Which ${sourceDisplayName}(s) provides this ${targetType}?`;
        } else if (
          relTypeLower === "consumes" ||
          relTypeLower === "is consumed by"
        ) {
          // Special case: for applications (target), show friendly label
          if (frontendTargetType === "ApplicationComponent") {
            incomingLabel = `Which interfaces does this application consume?`;
          } else {
            incomingLabel = `Which ${sourceDisplayName}(s) consumes this ${targetType}?`;
          }
        } else if (relTypeLower === "affects") {
          incomingLabel = `Which ${sourceDisplayName}(s) affects this ${targetType}?`;
        } else if (relTypeLower === "runs") {
          incomingLabel = `Which ${sourceDisplayName}(s) runs this ${targetType}?`;
        } else {
          incomingLabel = `Which ${sourceDisplayName}(s) ${relationshipType} this ${targetType}?`;
        }

        rulesByEntityType[key].push({
          key: `${rule.rule_key}_reverse`,
          visibleLabel: incomingLabel,
          relatedEntityType: frontendSourceType,
          direction: "incoming",
          relationshipType: relationshipType,
          description: rule.description,
          isActive: rule.is_active,
          isIncoming: true,
          originalSourceType: sourceType,
          originalTargetType: targetType,
        });
      });
    });

    return rulesByEntityType;
  }, [relationshipRules]);

  // Get rules for a specific entity type
  const getRulesForEntityType = (entityType) => {
    console.log("🔍 Getting rules for entity type:", entityType);
    console.log(
      "🔍 Available transformedRules keys:",
      Object.keys(transformedRules)
    );
    const rules = transformedRules[entityType] || [];
    console.log("🔍 Found rules:", rules);
    return rules;
  };

  // Get all available entity types that have relationship rules
  const getEntityTypesWithRules = () => {
    return Object.keys(transformedRules);
  };

  // Check if a relationship section should be shown
  const shouldShowRelationshipSection = (entityType, sectionName) => {
    const rules = getRulesForEntityType(entityType);
    return rules.some(
      (rule) =>
        rule.key === sectionName || rule.visibleLabel.includes(sectionName)
    );
  };

  return {
    relationshipRules,
    relationshipTypes,
    transformedRules,
    loading,
    error,
    getRulesForEntityType,
    getEntityTypesWithRules,
    shouldShowRelationshipSection,
    refetch: () => {
      setLoading(true);
      // Re-trigger the useEffect
      window.location.reload();
    },
  };
};

/**
 * Convert database entity type names to frontend format
 * Maps lowercase-with-hyphens to PascalCase
 */
/** Retired with Solution Component — do not surface as interface endpoint sections. */
const RETIRED_INTERFACE_ENDPOINT_RULE_KEYS = new Set([
  "interface_provided_by_sc",
  "interface_consumed_by_sc",
]);

const convertEntityTypeName = (dbName) => {
  const nameMap = {
    objective: "Goal",
    platform: "Platform",
    initiative: "Initiative",
    organization: "Organization",
    "business-context": "BusinessContext",
    application: "ApplicationComponent",
    "technology-component": "ITComponent",
    provider: "Provider",
    interface: "ApplicationInterface",
    "data-object": "DataObject",
    "business-capability": "BusinessCapability",
    "business-actor": "BusinessActor",
    "solution-component": "solution-component",
  };

  return nameMap[dbName] || dbName;
};

